/**
 * Staff Functions Context
 * Manages staff role-based access control for the admin panel
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const StaffFunctionsContext = createContext(null);

// Map sidebar item IDs to staff function IDs
const FUNCTION_MAPPING = {
  'overview': null, // Always visible
  'users': null, // Admin/CEO only
  'staff': 'staff', // Admin/CEO only (handled separately)
  'featured-stores': null, // Admin/CEO only
  'drivers': 'drivers',
  'businesses': 'businesses',
  'products': 'inventory',
  'inventory': 'inventory',
  'audit-logs': 'audit',
  'pricing': 'pricing',
  'orders': 'orders',
  'support': 'support',
  'finance': 'finance',
};

// Items that only admins/CEOs can see (not assignable to staff)
const ADMIN_ONLY_ITEMS = ['users', 'staff', 'featured-stores', 'overview'];

export const StaffFunctionsProvider = ({ children }) => {
  const { user, token, isAdmin } = useAuth();
  const [myFunctions, setMyFunctions] = useState([]);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchMyFunctions = useCallback(async () => {
    // Wait until we have both user and token
    if (!user || !token) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/admin/staff/my-functions');
      setMyFunctions(response.data?.functions || []);
      setIsAdminUser(response.data?.is_admin || false);
    } catch (error) {
      console.error('Error fetching functions:', error);
      // If admin check passes, assume admin
      if (isAdmin()) {
        setIsAdminUser(true);
        setMyFunctions(['inventory', 'orders', 'drivers', 'support', 'finance', 'audit', 'pricing', 'businesses', 'staff']);
      } else {
        setMyFunctions([]);
        setIsAdminUser(false);
      }
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [user, token, isAdmin]);

  useEffect(() => {
    fetchMyFunctions();
  }, [fetchMyFunctions]);

  // Don't report loading as false until we've actually tried to fetch
  const effectiveLoading = !hasFetched || loading;

  /**
   * Check if user has access to a specific sidebar item
   */
  const hasAccessToItem = useCallback((itemId) => {
    // Admins and CEOs have access to everything
    if (isAdminUser) return true;

    // Admin-only items are not accessible to regular staff
    if (ADMIN_ONLY_ITEMS.includes(itemId)) return false;

    // Get the function required for this item
    const requiredFunction = FUNCTION_MAPPING[itemId];
    
    // If no function required (null), allow access
    if (requiredFunction === null) return true;

    // Check if user has the required function
    return myFunctions.includes(requiredFunction);
  }, [isAdminUser, myFunctions]);

  /**
   * Check if user has a specific function
   */
  const hasFunction = useCallback((functionId) => {
    if (isAdminUser) return true;
    return myFunctions.includes(functionId);
  }, [isAdminUser, myFunctions]);

  /**
   * Filter sidebar items based on user's functions
   */
  const filterSidebarItems = useCallback((items) => {
    return items.filter(item => hasAccessToItem(item.id));
  }, [hasAccessToItem]);

  const value = {
    myFunctions,
    isAdminUser,
    loading: effectiveLoading,
    hasAccessToItem,
    hasFunction,
    filterSidebarItems,
    refreshFunctions: fetchMyFunctions,
  };

  return (
    <StaffFunctionsContext.Provider value={value}>
      {children}
    </StaffFunctionsContext.Provider>
  );
};

export const useStaffFunctions = () => {
  const context = useContext(StaffFunctionsContext);
  if (!context) {
    throw new Error('useStaffFunctions must be used within a StaffFunctionsProvider');
  }
  return context;
};

export default StaffFunctionsContext;
